import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, 
    TextField, Box, Typography, Divider, IconButton, Paper, CircularProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload'; 
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Page, Text, View, Document, StyleSheet, PDFDownloadLink, Font, Image } from '@react-pdf/renderer';

import { formatCurrency } from '../../utils/currencyUtils'; 
import { formatDateDisplay } from '../../utils/dateUtils';
import * as API from '../../api';

Font.register({
    family: 'Roboto',
    fonts: [
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf' },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 'bold' },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf', fontStyle: 'italic' }
    ]
});

const pdfStyles = StyleSheet.create({
    page: { padding: 30, fontFamily: 'Roboto', fontSize: 10 },
    headerContainer: { marginBottom: 20, textAlign: 'center' },
    title: { fontSize: 18, fontWeight: 'bold', textTransform: 'uppercase', color: '#007373', marginBottom: 5 },
    subTitle: { fontSize: 10, fontStyle: 'italic', color: '#666', marginBottom: 3 },
    groupContainer: { marginBottom: 15 },
    groupHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 5, borderBottomWidth: 1, borderBottomColor: '#000', paddingBottom: 5 },
    groupTitle: { fontSize: 12, fontWeight: 'bold', color: '#2c3e50' },
    groupBank: { fontSize: 10, color: '#555' },
    groupTotal: { fontSize: 12, fontWeight: 'bold', color: '#d32f2f' },
    tableHeader: { flexDirection: 'row', backgroundColor: '#f5f5f5', borderBottomWidth: 1, borderBottomColor: '#ccc', borderTopWidth: 1, borderTopColor: '#ccc', paddingTop: 5, paddingBottom: 5 },
    tableRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#eee', paddingTop: 5, paddingBottom: 5 },
    col1: { width: '22%', paddingLeft: 5 }, // Mã BK
    col2: { width: '28%', paddingLeft: 5 }, // Tên khách (giảm từ 40 xuống 28)
    col3: { width: '15%', textAlign: 'center' }, // Ngày đi
    colNew: { width: '10%', paddingLeft: 5 }, // CỘT MỚI: Loại DV
    col4: { width: '25%', textAlign: 'right', paddingRight: 5 }, // Số tiền (giữ nguyên để không bị nhảy dòng số tiền)
    boldText: { fontWeight: 'bold' },
    grandTotal: { marginTop: 20, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#000', textAlign: 'right', fontSize: 14, fontWeight: 'bold', color: '#d32f2f' },
    qrImage: { width: 200, height: 200, marginTop: 5, alignSelf: 'flex-end' }
});

const TYPE_LABELS = {
    'TOUR': 'Tour',
    'TRANSPORT': 'Xe',
    'ROOM': 'Phòng',
    'EXTRA': 'Dịch vụ khác',
    'COLLECTION': 'Thu hộ'
};

const PayablePDFDocument = ({ groupedData, filterDate, grandTotal, getFullImageUrl }) => (
    <Document>
        <Page size="A4" style={pdfStyles.page}>
            <View style={pdfStyles.headerContainer}>
                <Text style={pdfStyles.title}>TỔNG HỢP THANH TOÁN CÔNG NỢ</Text>
                <Text style={pdfStyles.subTitle}>
                    Giai đoạn: {filterDate?.isAllTime ? "Toàn thời gian" : `${formatDateDisplay(filterDate?.start)} - ${formatDateDisplay(filterDate?.end)}`}
                </Text>
                <Text style={pdfStyles.subTitle}>
                    Ngày lập phiếu: {new Date().toLocaleDateString('vi-VN')}
                </Text>
            </View>

            {groupedData.map((group, index) => (
                <View key={index} style={pdfStyles.groupContainer} wrap={false}>
                    <View style={pdfStyles.groupHeader}>
                        <View style={{ width: '70%' }}>
                            <Text style={pdfStyles.groupTitle}>{group.name}</Text>
                            {group.bank_info ? <Text style={pdfStyles.groupBank}>TK: {group.bank_info}</Text> : null}
                        </View>
                        <View style={{ width: '30%', alignItems: 'flex-end' }}>
                            <Text style={pdfStyles.groupTotal}>Tổng: {formatCurrency(group.totalAmount)}</Text>
                            {group.payment_qr && (
                                <Image 
                                    src={getFullImageUrl(group.payment_qr)} 
                                    style={pdfStyles.qrImage} 
                                />
                            )}
                        </View>
                    </View>

                    <View style={pdfStyles.tableHeader}>
                        <Text style={[pdfStyles.col1, pdfStyles.boldText]}>Mã BK</Text>
                        <Text style={[pdfStyles.col2, pdfStyles.boldText]}>Khách hàng</Text>
                        <Text style={[pdfStyles.col3, pdfStyles.boldText]}>Ngày đi</Text>
                        <Text style={[pdfStyles.colNew, pdfStyles.boldText]}>Loại DV</Text>
                        <Text style={[pdfStyles.col4, pdfStyles.boldText]}>Số tiền</Text>
                    </View>

                    {group.items.map((item, idx) => (
                        <View key={idx} style={pdfStyles.tableRow}>
                            <Text style={pdfStyles.col1}>{item.booking_code}</Text>
                            <Text style={pdfStyles.col2}>{item.customer_name}</Text>
                            <Text style={pdfStyles.col3}>{formatDateDisplay(item.start_date)}</Text>
                            <Text style={pdfStyles.colNew}>{TYPE_LABELS[item.payable_type] || item.payable_type || '-'}</Text>
                            <Text style={[pdfStyles.col4, pdfStyles.boldText]}>{formatCurrency(item.amount)}</Text>
                        </View>
                    ))}
                </View>
            ))}

            <View style={pdfStyles.grandTotal}>
                <Text>TỔNG CỘNG: {formatCurrency(grandTotal)} VND</Text>
            </View>
        </Page>
    </Document>
);

const PayableInfoDialog = ({ open, onClose, selectedPayables, suppliers, showToast, filterDate }) => {
    const [textContent, setTextContent] = useState('');
    const [groupedData, setGroupedData] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [grandTotal, setGrandTotal] = useState(0);

    const getFullImageUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        const apiBase = API.client?.defaults?.baseURL?.replace(/\/api\/?$/, '') || 'http://127.0.0.1:8000';
        return `${apiBase}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    useEffect(() => {
        if (open && selectedPayables && selectedPayables.length > 0) {
            
            const groups = {};
            let total = 0;

            selectedPayables.forEach(item => {
                const supId = typeof item.supplier === 'object' ? item.supplier.id : item.supplier;
                const supDetail = suppliers.find(s => s.id === supId) || {};
                const supName = item.supplier_name || supDetail.name || 'NCC Khác';
                
                if (!groups[supId]) {
                    groups[supId] = {
                        id: supId,
                        name: supName,
                        bank_info: supDetail.bank_info || '',
                        payment_qr: supDetail.payment_qr || '',
                        items: [],
                        totalAmount: 0
                    };
                }
                groups[supId].items.push(item);
                const amount = parseFloat(item.amount || 0);
                groups[supId].totalAmount += amount;
                total += amount;
            });

            const groupsArray = Object.values(groups);
            setGroupedData(groupsArray);
            setGrandTotal(total);

            let dateRangeStr = filterDate?.isAllTime 
                ? "Toàn thời gian" 
                : `${formatDateDisplay(filterDate?.start)} - ${formatDateDisplay(filterDate?.end)}`;

            let text = `TỔNG HỢP THANH TOÁN CÔNG NỢ\n`;
            text += `Giai đoạn: ${dateRangeStr}\n`;
            text += `Ngày lập: ${new Date().toLocaleDateString('vi-VN')}\n`;
            text += `====================================\n\n`;

            groupsArray.forEach((group, idx) => {
                text += `--------------------------------------------------\n`;
                text += `${idx + 1}. ${group.name.toUpperCase()} | Tổng: ${formatCurrency(group.totalAmount)}\n`;
                if (group.bank_info) text += `   (TK: ${group.bank_info})\n`;
                text += `   Chi tiết:\n`;
                
                group.items.forEach(bk => {
                    const custName = bk.customer_name || 'Khách lẻ';
                    text += `   - ${bk.booking_code} | ${custName} | ${formatDateDisplay(bk.start_date)}: ${formatCurrency(bk.amount)}\n`;
                });
                text += `\n`;
            });

            text += `====================================\n`;
            text += `TỔNG CỘNG THANH TOÁN: ${formatCurrency(total)} VND`;
            
            setTextContent(text);
            setShowPreview(false);
        }
    }, [open, selectedPayables, suppliers, filterDate]);

    const pdfFileName = (() => {
        const today = new Date();
        const d = String(today.getDate()).padStart(2, '0');
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const y = today.getFullYear();
        const createdDate = `${d}-${m}-${y}`;

        let period = 'ToanThoiGian';
        if (filterDate && !filterDate.isAllTime && filterDate.start && filterDate.end) {
            const s = filterDate.start.split('-').reverse().join('-');
            const e = filterDate.end.split('-').reverse().join('-');
            period = `${s}_${e}`;
        }
        return `TongHopCongNo_GiaiDoan${period}_${createdDate}.pdf`;
    })();

    const handleCopy = () => {
        navigator.clipboard.writeText(textContent);
        if (showToast) showToast('Đã copy nội dung công nợ!', 'success');
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth={showPreview ? "lg" : "md"} 
            fullWidth
        >
            <DialogTitle sx={{ bgcolor: '#007373', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    {showPreview ? 'XEM TRƯỚC BẢN IN (PDF)' : `SOẠN THẢO CÔNG NỢ (${selectedPayables.length} khoản)`}
                </Box>
                <Box>
                    {!showPreview ? (
                         <Button 
                            variant="contained" 
                            size="small"
                            color="warning" 
                            startIcon={<VisibilityIcon />} 
                            onClick={() => setShowPreview(true)}
                            sx={{ mr: 2, bgcolor: '#f57c00' }}
                         >
                            Xem PDF
                         </Button>
                    ) : (
                        <Button 
                            variant="contained" 
                            size="small"
                            color="secondary" 
                            startIcon={<ArrowBackIcon />} 
                            onClick={() => setShowPreview(false)}
                            sx={{ mr: 2 }}
                         >
                            Quay lại
                         </Button>
                    )}
                    <IconButton onClick={onClose} sx={{ color: 'white' }} size="small"><CloseIcon /></IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent dividers sx={{ p: 0, bgcolor: '#fff' }}>
                
                {!showPreview && (
                    <Box sx={{ display: 'flex', height: '500px' }}>
                        <Box sx={{ 
                            width: '35%', 
                            borderRight: '1px solid #eee', 
                            bgcolor: '#f8f9fa',
                            display: 'flex', 
                            flexDirection: 'column',
                            p: 0
                        }}>
                            <Box sx={{ p: 1.5, borderBottom: '1px solid #ddd', bgcolor: '#e0f2f1' }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="#00695c">
                                    DANH SÁCH CÔNG NỢ
                                </Typography>
                            </Box>
                            
                            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
                                {selectedPayables.map((item, idx) => (
                                    <Box key={idx} sx={{ 
                                        p: 1, mb: 1, 
                                        bgcolor: '#e0f2f1', 
                                        borderRadius: 1, 
                                        border: '1px solid #80cbc4',
                                    }}>
                                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#00695c', mb: 0.5, fontSize: '0.85rem' }}>
                                            #{idx + 1} - {item.supplier_name || 'NCC'}
                                        </Typography>
                                        
                                        <Typography variant="caption" display="block" color="textSecondary">
                                            Mã: {item.booking_code} 
                                        </Typography>
                                        <Typography variant="caption" display="block" color="textSecondary" noWrap>
                                            Khách: {item.customer_name}
                                        </Typography>
                                        
                                        <Typography variant="caption" display="block" sx={{fontWeight:'bold', color: '#d32f2f', mt: 0.5}}>
                                            Tổng: {formatCurrency(item.amount)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ p: 1.5, borderBottom: '1px solid #ddd' }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="#2c3e50">
                                    NỘI DUNG (Copy gửi Zalo/Mail):
                                </Typography>
                            </Box>
                            <TextField
                                multiline
                                fullWidth
                                value={textContent}
                                onChange={(e) => setTextContent(e.target.value)}
                                sx={{ 
                                    flex: 1,
                                    bgcolor: '#fafafa', 
                                    '& .MuiInputBase-root': { 
                                        height: '100%', 
                                        alignItems: 'flex-start',
                                        fontFamily: 'monospace', 
                                        p: 2,
                                        fontSize: '0.9rem'
                                    },
                                    '& fieldset': { border: 'none' }
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {showPreview && (
                    <Box sx={{ p: 4, height: '60vh', overflowY: 'auto', display: 'flex', justifyContent: 'center', bgcolor: '#525659' }}>
                         <Paper 
                            elevation={3}
                            sx={{ 
                                width: '210mm', 
                                minHeight: '297mm',
                                p: 5, 
                                bgcolor: 'white',
                                boxSizing: 'border-box'
                            }}
                        >
                            <Typography variant="h5" align="center" sx={{ fontWeight: 'bold', color: '#007373', mb: 1, textTransform: 'uppercase' }}>
                                TỔNG HỢP THANH TOÁN CÔNG NỢ
                            </Typography>
                            <Typography variant="body2" align="center" sx={{ mb: 0.5, fontStyle: 'italic', color: '#666' }}>
                                Giai đoạn: {filterDate?.isAllTime ? "Toàn thời gian" : `${formatDateDisplay(filterDate?.start)} - ${formatDateDisplay(filterDate?.end)}`}
                            </Typography>
                            <Typography variant="body2" align="center" sx={{ mb: 4, fontStyle: 'italic', color: '#666' }}>
                                Ngày lập phiếu: {new Date().toLocaleDateString('vi-VN')}
                            </Typography>

                            {groupedData.map((group) => (
                                <Box key={group.id} sx={{ mb: 4 }}>
                                    <Divider sx={{ mb: 2, borderColor: '#000', borderWidth: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                                                {group.name}
                                            </Typography>
                                            {group.bank_info && (
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#555' }}>
                                                    TK: {group.bank_info}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                                                Tổng: {formatCurrency(group.totalAmount)}
                                            </Typography>
                                            {group.payment_qr && (
                                                <Box sx={{ mt: 1 }}>
                                                    <img src={getFullImageUrl(group.payment_qr)} alt="QR" style={{ height: '60px', width: 'auto' }} />
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Mã BK</th>
                                                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Khách hàng</th>
                                                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Ngày đi</th>
                                                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Loại DV</th>
                                                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Số tiền</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>{item.booking_code}</td>
                                                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>{item.customer_name}</td>
                                                    <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatDateDisplay(item.start_date)}</td>
                                                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>{TYPE_LABELS[item.payable_type] || item.payable_type || '-'}</td>
                                                    <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(item.amount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Box>
                            ))}
                        </Paper>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #ddd' }}>
                <Button onClick={onClose} color="inherit" variant="outlined">Đóng</Button>
                
                {showPreview ? (
                    <PDFDownloadLink
                        document={
                            <PayablePDFDocument 
                                groupedData={groupedData} 
                                filterDate={filterDate} 
                                grandTotal={grandTotal}
                                getFullImageUrl={getFullImageUrl}
                            />
                        }
                        fileName={pdfFileName}
                    >
                        {({ loading }) => (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />} 
                                sx={{ bgcolor: '#2c3e50' }}
                                disabled={loading}
                            >
                                {loading ? 'Đang tạo PDF...' : 'Tải xuống PDF'}
                            </Button>
                        )}
                    </PDFDownloadLink>
                ) : (
                    <Button 
                        variant="contained" 
                        color="success"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopy} 
                    >
                        Copy Nội dung Text
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PayableInfoDialog;